import {
  addDays,
  differenceInCalendarDays,
  format,
  formatISO9075,
  parseISO,
  startOfToday,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { I18nLocale, LOCALE } from 'utils';

import { ShippingDeliverySettings } from '@/modules/product/api/productList';

import { getDateFnsLocale } from './date';

export const renderShippingTime = (
  shippingTime: number,
  locale: string,
  shippingDeliverySettings: ShippingDeliverySettings,
  holidaysCalendar: Record<string, boolean>
): { shipmentTime: string; isDate: boolean; shipmentDuration: number } => {
  const serverTimeZone = 'Europe/Warsaw';
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? serverTimeZone;

  const serverTimeUTC = zonedTimeToUtc(new Date(), userTimeZone);
  const serverTime = utcToZonedTime(serverTimeUTC, serverTimeZone);
  const isHoliDay = Object.values(holidaysCalendar)[0];
  let calculatedShippingTime = shippingTime;

  if (calculatedShippingTime === 0) {
    if (!isHoliDay) {
      calculatedShippingTime += shippingDeliverySettings.isCutoff;
    }

    calculatedShippingTime += shippingDeliverySettings.immediateShippingDelay;
  }

  let numberOfSpecialDays = 0;

  Object.keys(holidaysCalendar).every((key, index) => {
    if (index >= calculatedShippingTime) {
      return false;
    }

    if (holidaysCalendar[key]) {
      return (numberOfSpecialDays += 1);
    }

    return true;
  });

  let shipmentDate = formatISO9075(startOfToday());

  Object.keys(holidaysCalendar).every((key, index) => {
    if (index < calculatedShippingTime) {
      return true;
    }

    if (holidaysCalendar[key]) {
      return true;
    }

    if (numberOfSpecialDays) {
      numberOfSpecialDays -= 1;
      return true;
    }

    shipmentDate = key;
    return false;
  });

  const parsedShipmentDate = parseISO(shipmentDate);

  const difference = differenceInCalendarDays(parsedShipmentDate, serverTime);

  const calculatedShipmentDate = addDays(serverTime, difference);
  const calculatedShipmentDateUtc = zonedTimeToUtc(
    calculatedShipmentDate,
    serverTimeZone
  );
  const calculatedShipmentDateUserZone = utcToZonedTime(
    calculatedShipmentDateUtc,
    userTimeZone
  );

  const dateLocale = getDateFnsLocale(locale as I18nLocale);

  let dateFormat = 'EEEE dd.MM';
  if (locale === LOCALE.DE) {
    dateFormat = 'EEEE, dd.MM';
  }

  const getDayString = format(calculatedShipmentDateUserZone, dateFormat, {
    locale: dateLocale,
  });

  const getFormattedShippingTime = () => {
    switch (difference) {
      case 0:
        return {
          shipmentTime: 'common.today',
          isDate: false,
          shipmentDuration: difference,
        };
      case 1:
        return {
          shipmentTime: 'common.tomorrow',
          isDate: false,
          shipmentDuration: difference,
        };
      case 2:
        return {
          shipmentTime: 'common.afterTomorrow',
          isDate: false,
          shipmentDuration: difference,
        };
      default:
        return {
          shipmentTime: getDayString,
          isDate: true,
          shipmentDuration: difference,
        };
    }
  };

  return getFormattedShippingTime();
};

export const renderDeliveryTime = (
  shipmentDuration: number,
  deliveryDuration: number,
  locale: string,
  shippingDeliverySettings: ShippingDeliverySettings,
  localeCalendar: Record<string, boolean>
): { deliveryTime: string; isDate: boolean; deliveryDuration: number } => {
  const serverTimeZone = 'Europe/Warsaw';
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? serverTimeZone;

  const serverTimeUTC = zonedTimeToUtc(new Date(), userTimeZone);
  const serverTime = utcToZonedTime(serverTimeUTC, serverTimeZone);

  let calculatedDeliveryTime =
    shipmentDuration +
    shippingDeliverySettings.logisticDelay +
    deliveryDuration;

  let shipmentDate = formatISO9075(startOfToday());

  Object.keys(localeCalendar).every((key, index) => {
    if (index < calculatedDeliveryTime) {
      return true;
    }

    if (localeCalendar[key]) {
      return true;
    }

    shipmentDate = key;
    return false;
  });

  const parsedDeliveryDate = parseISO(shipmentDate);

  const difference = differenceInCalendarDays(parsedDeliveryDate, serverTime);

  const calculatedDeliveryDate = addDays(serverTime, difference);
  const calculatedDeliveryDateUtc = zonedTimeToUtc(
    calculatedDeliveryDate,
    serverTimeZone
  );
  const calculatedDeliveryDateUserZone = utcToZonedTime(
    calculatedDeliveryDateUtc,
    userTimeZone
  );

  const dateLocale = getDateFnsLocale(locale as I18nLocale);

  let dateFormat = 'EEEE dd.MM';
  if (locale === LOCALE.DE) {
    dateFormat = 'EEEE, dd.MM';
  }

  const getDayString = format(calculatedDeliveryDateUserZone, dateFormat, {
    locale: dateLocale,
  });

  const getFormattedDeliveryTime = () => {
    switch (difference) {
      case 0:
        return {
          deliveryTime: 'common.today',
          isDate: false,
          deliveryDuration: difference,
        };
      case 1:
        return {
          deliveryTime: 'common.tomorrow',
          isDate: false,
          deliveryDuration: difference,
        };
      case 2:
        return {
          deliveryTime: 'common.afterTomorrow',
          isDate: false,
          deliveryDuration: difference,
        };
      default:
        return {
          deliveryTime: getDayString,
          isDate: true,
          deliveryDuration: difference,
        };
    }
  };

  return getFormattedDeliveryTime();
};
